import { action, computed, makeObservable } from 'mobx'

import { UploadLogFilterType } from '~/client/graph'
import DesktopEventsStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { UNASSIGNED_FILTER_OPTION } from '~/client/src/shared/components/Deliveries/DeliveriesView.store'
import { getAttachmentFileType } from '~/client/src/shared/components/WorkflowCard/fileAttachmentHelpers'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { RESET_ALL_FILTERS } from '~/client/src/shared/stores/EventStore/eventConstants'
import UIFilterInfo from '~/client/src/shared/stores/substates/UIFilterInfo'
import { LOCATION_SEPARATOR } from '~/client/src/shared/utils/usefulStrings'

import AttachmentsStore from '../../../../Attachments.store'
import BaseAttachmentFilterStore, {
  ISourceMap,
} from './BaseAttachmentFilter.store'

const attachmentFilterTypes = [
  UploadLogFilterType.Filetype,
  UploadLogFilterType.FileExtension,
  UploadLogFilterType.WorkflowCategory,
  UploadLogFilterType.Formtype,
  UploadLogFilterType.Location,
  UploadLogFilterType.Company,
  UploadLogFilterType.UploadedBy,
]

const FILTER_TYPES_WITHOUT_UNASSIGNED = [
  UploadLogFilterType.Filetype,
  UploadLogFilterType.FileExtension,
]

function getAttachmentFilterTypeCaption(
  filterType: UploadLogFilterType,
): string {
  switch (filterType) {
    case UploadLogFilterType.Company:
      return Localization.translator.company
    case UploadLogFilterType.Location:
      return Localization.translator.location
    case UploadLogFilterType.Source:
      return Localization.translator.workflowId
    case UploadLogFilterType.Filetype:
      return Localization.translator.fileCategory
    case UploadLogFilterType.FileExtension:
      return Localization.translator.fileType
    case UploadLogFilterType.Formtype:
      return Localization.translator.workflowType
    case UploadLogFilterType.WorkflowCategory:
      return Localization.translator.workflowCategory
    case UploadLogFilterType.DateUploaded:
      return Localization.translator.dateUploaded
    case UploadLogFilterType.UploadedBy:
      return Localization.translator.uploadedBy
    default:
      return filterType
  }
}

export default class AttachmentFilterStore {
  public constructor(
    private readonly eventsStore: DesktopEventsStore,
    private readonly attachmentsStore: AttachmentsStore,
    private readonly onShowChanged: (
      isShown: boolean,
      filterType: string,
    ) => void,
    private readonly companiesStore: CompaniesStore,
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly tagsStore: TagsStore,
    private readonly workflowCategoriesStore: WorkflowCategoriesStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly workflows: Map<string, Workflow>,
    private readonly totalFacets?: any,
  ) {
    makeObservable(this)
  }

  @action.bound
  public syncFilters() {
    Object.keys(this.fieldsMap).forEach(filterType => {
      const filter = this.fieldsMap[filterType]
      const sourceMap = this.sourceMapByFilterTypeMap[filterType]
      this.updateCommonFilterState(filter, sourceMap)
    })
  }

  @action.bound
  private updateCommonFilterState(filter: UIFilterInfo, sourceMap: ISourceMap) {
    let appliedFilterOptions = []

    filter.initialFilterOptions.forEach((_, optionKey) => {
      const deliveriesIds = sourceMap[optionKey]

      filter.selectedFilterOptions.set(optionKey, deliveriesIds)
      filter.initialFilterOptions.set(optionKey, deliveriesIds)

      appliedFilterOptions = appliedFilterOptions.concat(deliveriesIds)
    })

    filter.appliedFilterOptions = appliedFilterOptions
  }

  @computed
  public get filterStoresByTypeMap(): {
    [filterType: string]: BaseAttachmentFilterStore
  } {
    const map: { [filterType: string]: BaseAttachmentFilterStore } = {}

    attachmentFilterTypes.forEach(filterType => {
      const { appState } = this.eventsStore

      map[filterType] = new BaseAttachmentFilterStore(
        filterType,
        appState,
        () => this.sourceMapByFilterTypeMap[filterType],
        this.attachmentsStore,
        this.onShowChanged,
        this.fieldsMap(),
        getAttachmentFilterTypeCaption,
        this.getOptionName,
        LocalStorageKey.AttachmentsFilter,
        (type, optionKey) =>
          this.getFilterOptionCounts(type as UploadLogFilterType, optionKey),
      )
    })
    return map
  }

  @action.bound
  public resetAppliedCustomFilterId() {
    this.eventsStore.appState.filters.appliedCustomFilterId = null
  }

  @action.bound
  public resetAllFilters() {
    attachmentFilterTypes.forEach(type =>
      localStorage.removeItem(
        `${this.eventsStore.appState.activeProject.id}_${LocalStorageKey.AttachmentsFilter}_${type}`,
      ),
    )
    this.eventsStore.dispatch(RESET_ALL_FILTERS)
  }

  @computed
  protected get sourceMapByFilterTypeMap(): {
    [filterType: string]: ISourceMap
  } {
    const maps = attachmentFilterTypes.reduce((acc, filterType) => {
      acc[filterType] = this.buildSourceMapFromFacets(filterType)
      return acc
    }, {})

    return maps
  }

  private buildSourceMapFromFacets(
    filterType: UploadLogFilterType,
  ): ISourceMap {
    const totalFacets = this.getTotalFacetsForFilterType(filterType)
    const sourceMap: ISourceMap = {}

    totalFacets.forEach(facet => {
      if (facet?.key != null) {
        const totalCount = facet.count || 0

        if (totalCount > 0) {
          sourceMap[facet.key] = []
        }
      }
    })

    if (!FILTER_TYPES_WITHOUT_UNASSIGNED.includes(filterType)) {
      if (!sourceMap[UNASSIGNED_FILTER_OPTION]) {
        sourceMap[UNASSIGNED_FILTER_OPTION] = []
      }
    }

    return sourceMap
  }

  public getFilterOptionCounts(
    filterType: UploadLogFilterType,
    optionKey: string,
  ): { viewable: number; total: number } {
    const totalFacets = this.getTotalFacetsForFilterType(filterType)
    const viewableFacets = this.getViewableFacetsForFilterType(filterType)

    const totalFacet = totalFacets.find(f => f?.key === optionKey)
    const viewableFacet = viewableFacets.find(f => f?.key === optionKey)

    return {
      viewable: viewableFacet?.count || 0,
      total: totalFacet?.count || 0,
    }
  }

  private getTotalFacetsForFilterType(filterType: UploadLogFilterType) {
    if (!this.attachmentsStore.totalFacets) {
      return []
    }

    switch (filterType) {
      case UploadLogFilterType.Company:
        return this.attachmentsStore.totalFacets.companyFilters || []
      case UploadLogFilterType.Location:
        return this.attachmentsStore.totalFacets.locationFilters || []
      case UploadLogFilterType.UploadedBy:
        return this.attachmentsStore.totalFacets.userFilters || []
      case UploadLogFilterType.WorkflowCategory:
        return this.attachmentsStore.totalFacets.workflowCategoryFilters || []
      case UploadLogFilterType.Formtype:
        return this.attachmentsStore.totalFacets.workflowTypeFilters || []
      case UploadLogFilterType.Filetype:
        return this.attachmentsStore.totalFacets.fileCategoryFilters || []
      case UploadLogFilterType.FileExtension:
        return this.attachmentsStore.totalFacets.fileTypeFilters || []
      case UploadLogFilterType.Source:
      case UploadLogFilterType.DateUploaded:
        console.warn(
          `Filter type ${filterType} doesn't have corresponding facets`,
        )
        return []
      default:
        return []
    }
  }

  private getViewableFacetsForFilterType(filterType: UploadLogFilterType) {
    if (!this.attachmentsStore.facets) {
      return []
    }

    switch (filterType) {
      case UploadLogFilterType.Company:
        return this.attachmentsStore.facets.companyFilters || []
      case UploadLogFilterType.Location:
        return this.attachmentsStore.facets.locationFilters || []
      case UploadLogFilterType.UploadedBy:
        return this.attachmentsStore.facets.userFilters || []
      case UploadLogFilterType.WorkflowCategory:
        return this.attachmentsStore.facets.workflowCategoryFilters || []
      case UploadLogFilterType.Formtype:
        return this.attachmentsStore.facets.workflowTypeFilters || []
      case UploadLogFilterType.Filetype:
        return this.attachmentsStore.facets.fileCategoryFilters || []
      case UploadLogFilterType.FileExtension:
        return this.attachmentsStore.facets.fileTypeFilters || []
      case UploadLogFilterType.Source:
      case UploadLogFilterType.DateUploaded:
        return []
      default:
        return []
    }
  }

  protected fieldsMap() {
    return this.eventsStore.appState.attachmentsFilters.fieldsMap
  }

  protected getOptionName = (option: string, filterType: string): string => {
    switch (filterType) {
      case UploadLogFilterType.Company:
        return this.companiesStore.getCompanyNameById(
          option,
          UNASSIGNED_FILTER_OPTION,
        )
      case UploadLogFilterType.Location:
        const [, attributeId] = option.split(LOCATION_SEPARATOR)
        // By id: the kind half names the store the location sat in when the key was
        // written, which a re-kind changes and a project-added kind has none of (STX1-597)
        return (
          this.tagsStore.getLocationByIdWithDeleted(attributeId)?.name || option
        )
      case UploadLogFilterType.Source:
        if (option === UNASSIGNED_FILTER_OPTION) {
          return Localization.translator.unassigned
        }
        const workflow = this.workflows.get(option)
        return workflow ? `${option} (${workflow.title})` : option
      case UploadLogFilterType.Formtype:
        if (option === UNASSIGNED_FILTER_OPTION) {
          return Localization.translator.unassigned
        }
        const formWorkflow = this.workflowTypesStore.getPermitTypeById(option)
        return formWorkflow ? `${formWorkflow.name}` : option
      case UploadLogFilterType.Filetype:
        return option
      case UploadLogFilterType.FileExtension:
        if (option === UNASSIGNED_FILTER_OPTION) {
          return Localization.translator.unassigned
        }
        const fileType = getAttachmentFileType(option, '/', true)
        return fileType.toUpperCase()
      case UploadLogFilterType.DateUploaded:
        if (option === UNASSIGNED_FILTER_OPTION) {
          return Localization.translator.unassigned
        }
        const date = new Date(option + 'T00:00:00')
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString()
        }
        return option
      case UploadLogFilterType.UploadedBy:
        if (option === UNASSIGNED_FILTER_OPTION) {
          return Localization.translator.unassigned
        }
        const user = this.projectMembersStore.getById(option)
        return user ? user.fullName : option
      case UploadLogFilterType.WorkflowCategory:
        return this.workflowCategoriesStore.getById(option)?.name || option
    }
  }
}
